import React, { useState, useEffect } from 'react'
import { CheckCircleIcon, ExclamationTriangleIcon, ArrowTopRightOnSquareIcon, FingerPrintIcon } from '@heroicons/react/24/outline'
import { useMercadoLibreOAuth } from '../hooks/useMercadoLibreOAuth'
import { useMercadoLibreTokenStatus } from '../hooks/useMercadoLibreTokenStatus'

// Componente para la cuenta regresiva del token
const TokenCountdownTimer: React.FC<{ minutesUntilExpiry: number }> = ({ minutesUntilExpiry }) => {
  const [timeLeft, setTimeLeft] = useState<{ hours: number; minutes: number; seconds: number }>({
    hours: 0,
    minutes: 0,
    seconds: 0
  })
  const [isExpired, setIsExpired] = useState(false)

  useEffect(() => {
    if (minutesUntilExpiry <= 0) {
      setIsExpired(true)
      return
    }

    // Calcular tiempo inicial correctamente
    const totalMinutes = minutesUntilExpiry
    const hours = Math.floor(totalMinutes / 60)
    const minutes = totalMinutes % 60
    
    setTimeLeft({ hours, minutes, seconds: 0 })
    setIsExpired(false)

    // Convertir minutos a milisegundos para el countdown
    const totalMs = minutesUntilExpiry * 60 * 1000
    let remainingMs = totalMs

    const interval = setInterval(() => {
      remainingMs -= 1000

      if (remainingMs <= 0) {
        setIsExpired(true)
        clearInterval(interval)
        return
      }

      const hours = Math.floor(remainingMs / (1000 * 60 * 60))
      const minutes = Math.floor((remainingMs % (1000 * 60 * 60)) / (1000 * 60))
      const seconds = Math.floor((remainingMs % (1000 * 60)) / 1000)

      setTimeLeft({ hours, minutes, seconds })
    }, 1000)

    return () => clearInterval(interval)
  }, [minutesUntilExpiry])

  if (isExpired) {
    return (
      <span className="ml-2 font-medium text-red-600">
        Expirado
      </span>
    )
  }

  const formatTime = (value: number) => value.toString().padStart(2, '0')

  return (
    <span className="ml-2 font-medium text-blue-600">
      {timeLeft.hours > 0 && `${formatTime(timeLeft.hours)}h `}
      {formatTime(timeLeft.minutes)}m {formatTime(timeLeft.seconds)}s
    </span>
  )
}

interface MercadoLibreTokenProps {}

const MercadoLibreToken: React.FC<MercadoLibreTokenProps> = () => {
  const {
    isLoading: oauthLoading,
    error: oauthError,
    getAuthUrl,
    openAuthWindow,
    clearError: clearOAuthError
  } = useMercadoLibreOAuth()

  const {
    tokenStatus,
    isLoading: statusLoading,
    error: statusError,
    loadTokenStatus,
    clearError: clearStatusError
  } = useMercadoLibreTokenStatus()

  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)
  const [authUrl, setAuthUrl] = useState('')

  // Cargar URL de autorización al montar el componente (solo una vez)
  useEffect(() => {
    const loadAuthUrl = async () => {
      const response = await getAuthUrl()
      if (response?.data?.authUrl) {
        setAuthUrl(response.data.authUrl)
      }
    }
    loadAuthUrl()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Solo se ejecuta una vez al montar el componente

  const clearMessage = () => {
    setMessage(null)
  }

  const handleOpenAuth = () => {
    if (authUrl) {
      openAuthWindow(authUrl)
    }
  }



  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center">
            <FingerPrintIcon className="h-6 w-6 text-blue-600 mr-3" />
            <h1 className="text-xl font-semibold text-gray-900">Token Mercado Libre</h1>
          </div>
          <p className="text-sm text-gray-600 mt-1">
            Configura los tokens de autenticación para la integración con MercadoLibre
          </p>
        </div>

        <div className="p-6">
          {/* Sección OAuth */}
          <div className={`mb-8 border rounded-lg p-6 ${
            tokenStatus && !tokenStatus.requiresAuth 
              ? 'bg-gray-50 border-gray-200' 
              : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200'
          }`}>
{/*             <div className="flex items-center mb-4">
              <ArrowTopRightOnSquareIcon className={`h-6 w-6 mr-3 ${
                tokenStatus && !tokenStatus.requiresAuth 
                  ? 'text-gray-400' 
                  : 'text-blue-600'
              }`} />
              <h2 className={`text-lg font-semibold ${
                tokenStatus && !tokenStatus.requiresAuth 
                  ? 'text-gray-500' 
                  : 'text-gray-900'
              }`}>
                Autorización OAuth
              </h2>
            </div> */}
            

{/*             <p className="text-sm text-gray-600 mb-4">
              Para obtener los tokens automáticamente, autoriza la aplicación con MercadoLibre:
            </p> */}

            {/* URL de autorización con botones */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                URL de Autorización
              </label>
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={authUrl}
                  readOnly
                  className={`flex-1 px-3 py-2 border rounded-lg text-sm ${
                    tokenStatus && !tokenStatus.requiresAuth
                      ? 'bg-gray-100 border-gray-300 text-gray-400'
                      : 'bg-gray-50 border-gray-300 text-gray-600'
                  }`}
                  placeholder="Cargando URL de autorización..."
                />
                <button
                  onClick={handleOpenAuth}
                  disabled={!authUrl || oauthLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  <ArrowTopRightOnSquareIcon className="h-4 w-4 mr-2" />
                  Autorizar
                </button>
                <a
                  href={authUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 flex items-center"
                  title="Abrir en Nueva Pestaña"
                >
                  <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                </a>
              </div>
            </div>

            {oauthError && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">{oauthError}</p>
                <button
                  onClick={clearOAuthError}
                  className="mt-1 text-sm text-red-600 hover:text-red-800"
                >
                  Cerrar
                </button>
              </div>
            )}
          </div>

          {(message || oauthError) && (
            <div className={`mb-6 p-4 rounded-lg flex items-center ${
              (message?.type === 'success' || !message) && !oauthError
                ? 'bg-green-50 text-green-800 border border-green-200' 
                : 'bg-red-50 text-red-800 border border-red-200'
            }`}>
              {(message?.type === 'success' || !message) && !oauthError ? (
                <CheckCircleIcon className="h-5 w-5 mr-2" />
              ) : (
                <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
              )}
              <span className="flex-1">{message?.text || oauthError}</span>
              <button
                onClick={clearMessage}
                className="text-gray-400 hover:text-gray-600 ml-2"
              >
                ×
              </button>
            </div>
          )}

          {/* Estado del token */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Estado del Token</h3>
              <button
                onClick={loadTokenStatus}
                disabled={statusLoading}
                className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 disabled:opacity-50"
              >
                {statusLoading ? 'Actualizando...' : 'Actualizar'}
              </button>
            </div>
            
            {statusError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">{statusError}</p>
                <button
                  onClick={clearStatusError}
                  className="mt-1 text-sm text-red-600 hover:text-red-800"
                >
                  Cerrar
                </button>
              </div>
            )}

            {statusLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              </div>
            ) : tokenStatus ? (
              <div className="space-y-4">
                {/* Estado general */}
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className={`w-3 h-3 rounded-full ${
                      tokenStatus.requiresAuth 
                        ? 'bg-red-400' 
                        : tokenStatus.requiresRefresh 
                        ? 'bg-yellow-400' 
                        : 'bg-green-400'
                    }`}></div>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">
                      {tokenStatus.requiresAuth 
                        ? 'No autorizado' 
                        : tokenStatus.requiresRefresh 
                        ? 'Requiere renovación' 
                        : 'Autorizado y activo'
                      }
                    </p>
                    <p className="text-sm text-gray-500">
                      User ID: {tokenStatus.userId}
                    </p>
                  </div>
                </div>

                {/* Detalles del token */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Detalles del Token</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Estado:</span>
                      <span className={`ml-2 font-medium ${
                        tokenStatus.tokenStatus.isExpired ? 'text-red-600' : 'text-green-600'
                      }`}>
                        {tokenStatus.tokenStatus.isExpired ? 'Expirado' : 'Válido'}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Tipo:</span>
                      <span className="ml-2 font-medium">{tokenStatus.tokenStatus.tokenType}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Expira:</span>
                      <span className="ml-2 font-medium">
                        {new Date(tokenStatus.tokenStatus.expiresAt).toLocaleString()}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Tiempo restante:</span>
                      <TokenCountdownTimer minutesUntilExpiry={tokenStatus.tokenStatus.minutesUntilExpiry} />
                    </div>
                    <div>
                      <span className="text-gray-500">Refresh Token:</span>
                      <span className={`ml-2 font-medium ${
                        tokenStatus.tokenStatus.hasRefreshToken ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {tokenStatus.tokenStatus.hasRefreshToken ? 'Disponible' : 'No disponible'}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Última actualización:</span>
                      <span className="ml-2 font-medium">
                        {new Date(tokenStatus.tokenStatus.lastUpdated).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500">No se pudo cargar el estado del token</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default MercadoLibreToken
